import { create } from 'zustand';
import type { RegistrationType } from './types';
import { persist } from 'zustand/middleware';
import { REGISTRATION_STORE_KEY } from '@/shared/config';
import { startRegistration as startRegistrationRequest } from '@/features/auth-signup';
import { turnstileManager } from '@/shared/lib/turnstile';
import type { InternalAxiosRequestConfig } from 'axios';

type RegistrationState = {
  registrationType: RegistrationType;
  setRegistrationType: (type: RegistrationType) => void;

  registrationStates: Record<RegistrationType, string>;

  setRegistrationState: (tab: RegistrationType, state: string) => void;

  setActiveRegistrationState: (state: string) => void;

  getActiveRegistrationState: () => string;
  getRegistrationState: (tab: RegistrationType) => string;

  handleRegistrationStateRefresh: (
    originalRequest: InternalAxiosRequestConfig & { _retry?: boolean }
  ) => Promise<InternalAxiosRequestConfig>;

  clearRegistrationState: () => void;
};

export const useRegistrationStore = create<RegistrationState>()(
  persist(
    (set, get) => ({
      registrationType: 'personal',
      setRegistrationType: type => set({ registrationType: type }),

      registrationStates: {
        personal: '',
        corporate: '',
      },

      setRegistrationState: (tab, registrationState) =>
        set(state => ({
          registrationStates: {
            ...state.registrationStates,
            [tab]: registrationState,
          },
        })),

      setActiveRegistrationState: state =>
        set({
          registrationStates: {
            ...get().registrationStates,
            [get().registrationType]: state,
          },
        }),

      getActiveRegistrationState: () =>
        get().registrationStates[get().registrationType],
      getRegistrationState: tab => get().registrationStates[tab],

      handleRegistrationStateRefresh: async (
        originalRequest: InternalAxiosRequestConfig & { _retry?: boolean }
      ) => {
        originalRequest._retry = true;

        try {
          const registrationType = get().registrationType;

          const captchaToken = await turnstileManager
            .getToken()
            .catch(() => '');

          const newState = await startRegistrationRequest({
            userType: registrationType,
            captchaToken,
          });

          get().setRegistrationState(registrationType, newState.state);

          const url = new URL(originalRequest.url!, originalRequest.baseURL);

          url.searchParams.set(
            'state',
            useRegistrationStore.getState().getActiveRegistrationState()
          );

          originalRequest.url = url.pathname + url.search;

          return originalRequest;
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      },

      clearRegistrationState: () =>
        set({
          registrationType: 'personal',
          registrationStates: { personal: '', corporate: '' },
        }),
    }),
    {
      name: REGISTRATION_STORE_KEY,
    }
  )
);
